/*
 *  -----------------------------------------------------------------------
 *    <copyright file="reports-templates-grid.data.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { ReportModules } from '@interfaces/report-templates.interface';
import { TFunction } from 'i18next';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '@components/core/grid-filters';
import { useGridCells } from '@hooks/index';
import Constants from '@utils/constants';

const { DateCell, YesNoCell, MaxLengthCell } = useGridCells();

export const TEMPLATE_GRID_COLUMNS = (t: TFunction, module: ReportModules): IExpandableGridColumnV2[] => {
    const isCM = module === Constants.Modules.CM;

    const defaultCols = [
        {
            label: t('type'),
            field: 'templateTypeName',
            minWidth: 100,
            id: 'reports-templates-type',
        },
        {
            label: t('format'),
            field: 'reportFormatName',
            minWidth: 120,
            id: 'reports-templates-format',
        },
        {
            label: t('cm:templateInUse'),
            field: 'inUse',
            minWidth: 135,
            id: 'reports-templates-templateInUse',
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('cm:templateName'),
            field: 'fullName',
            minWidth: Constants.ColumnWidth.default,
            id: 'reports-templates-name',
            cells: { data: MaxLengthCell(50) },
        },
        {
            label: t('description'),
            field: 'description',
            minWidth: Constants.ColumnWidth.default,
            id: 'reports-templates-description',
            cells: { data: MaxLengthCell(50) },
        },
    ];

    const createdUpdatedCols = [
        {
            label: t('ra:createdBy'),
            field: 'createdByName',
            headerClass: 'border',
            id: 'reports-templates-createdby',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            label: t('ra:createdDate'),
            field: 'createdDate',
            headerClass: 'border',
            filter: 'date',
            id: 'reports-templates-created',
            showByDefault: false,
            minWidth: 125,
            cells: { data: DateCell },
            filterUi: GridColumnDateFilter,
        },
        {
            label: t('ra:updatedBy'),
            field: 'updatedByName',
            headerClass: 'border',
            id: 'reports-templates-updatedby',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            label: t('ra:updatedDate'),
            field: 'updatedDate',
            headerClass: 'border',
            filter: 'date',
            id: 'reports-templates-updated',
            showByDefault: false,
            minWidth: 125,
            cells: { data: DateCell },
            filterUi: GridColumnDateFilter,
        },
        {
            label: t('reports:templateInUseUpdatedBy'),
            field: 'organizationReportTemplateUpdatedByName',
            headerClass: 'border',
            id: 'template-in-use-updated-by',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
        },
        {
            label: t('reports:templateInUseUpdatedDate'),
            field: 'organizationReportTemplateUpdatedDate',
            headerClass: 'border',
            filter: 'date',
            id: 'template-in-use-updated-date',
            showByDefault: false,
            minWidth: Constants.ColumnWidth.default,
            cells: { data: DateCell },
            filterUi: GridColumnDateFilter,
        },
    ];

    const recordIdCols: IExpandableGridColumnV2[] = [
        {
            field: '_itemId',
            label: t('reports:reportTemplateID'),
            headerClass: 'border',
            showByDefault: false,
            id: 'reports-templates--id',
            minWidth: 150,
        },
        {
            field: '_orgDbId',
            label: t('reports:organizationTemplateId'),
            headerClass: 'border',
            showByDefault: false,
            id: 'organization-template-id',
            minWidth: 150,
        },
    ];

    const signatoryCol = {
        label: t('cm:inclSignatures'),
        field: 'includeSignatures',
        minWidth: 140,
        id: 'reports-templates-inclSignatures',
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    };

    const nameAndVarsionCol = {
        label: t('cm:inclNameVersion'),
        field: 'includeNameAndVersion',
        minWidth: 145,
        id: 'reports-templates-inclNameVersion',
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    };

    const CMCols = [
        {
            label: t('cm:inclComparisonDataset'),
            field: 'includeComparisonDataset',
            minWidth: 175,
            id: 'reports-templates-inclComparisonDataset',
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        signatoryCol,
        nameAndVarsionCol,
    ];

    const RAMCols = [
        {
            label: t('cm:inclNotApplicableRisks'),
            field: 'includeNotApplicableRisks',
            minWidth: 125,
            id: 'reports-templates-inclInternalRisks',
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        signatoryCol,
        {
            label: t('cm:inclReviews'),
            field: 'includeReviews',
            minWidth: 125,
            id: 'reports-templates-includeReviews',
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        nameAndVarsionCol,
    ];

    return [...defaultCols, ...(isCM ? CMCols : RAMCols), ...createdUpdatedCols, ...recordIdCols];
};
